import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  LogOut,
  Loader2,
  Pencil,
  Trophy,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button, Card, CardContent, Input } from '@/components/ui'
import { MissionCompleteModal, type MissionCompleteData } from '@/components/missions'
import { useAuth } from '@/context/AuthContext'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import { useStudio } from '@/context/StudioContext'
import { StudioModeIndicator } from '@/components/StudioModeIndicator'
import { UpgradeBanner } from '@/components/UpgradeBanner'
import { completeMissionWithCelebration } from '@/services/missionService'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type { StepType, Mission } from '@/types/database'
import type { StoryRecord, StepRecord } from '@/types/domain'
import { StorySecretNudge } from '@/components/storytelling/StorySecretNudge'
import { loadSecrets, getRelevantSecrets } from '@/services/storySecretService'
import type { SecretNudge as SecretNudgeType, WritingSceneType } from '@/types/storytelling'

// Step configuration with kid-friendly prompts
const STEP_CONFIG: Record<
  StepType,
  { label: string; emoji: string; prompt: string; color: string }
> = {
  beginning: {
    label: 'Beginning',
    emoji: '🌅',
    prompt: 'Introduce your characters and setting. Where does your story take place? Who is your main character?',
    color: 'var(--color-kids-primary)',
  },
  problem: {
    label: 'Problem',
    emoji: '😰',
    prompt: 'What problem or challenge does your character face? What makes them worried or excited?',
    color: 'var(--color-kids-secondary)',
  },
  adventure: {
    label: 'Adventure',
    emoji: '🗺️',
    prompt: 'Your character goes on a journey to solve the problem. What happens along the way?',
    color: 'var(--color-kids-accent)',
  },
  climax: {
    label: 'Climax',
    emoji: '⚡',
    prompt: 'The most exciting moment! How does your character face the biggest challenge?',
    color: 'var(--color-kids-warning)',
  },
  resolution: {
    label: 'Resolution',
    emoji: '🎉',
    prompt: 'How does everything work out? What did your character learn? The End!',
    color: 'var(--color-kids-success)',
  },
}

const STEP_ORDER: StepType[] = ['beginning', 'problem', 'adventure', 'climax', 'resolution']

// Autosave debounce time in ms
const AUTOSAVE_DELAY = 1500

export function WriteStory() {
  const { storyId } = useParams<{ storyId: string }>()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const { activeWriter, setActiveWriter } = useFamily()
  const { showToast } = useToast()
  const { repository, mode, readOnly } = useStudio()

  const [story, setStory] = useState<StoryRecord | null>(null)
  const [steps, setSteps] = useState<StepRecord[]>([])
  const [showCompletionBanner, setShowCompletionBanner] = useState(false)
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [secretNudge, setSecretNudge] = useState<SecretNudgeType | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleInput, setTitleInput] = useState('')

  // Mission tracking
  const [activeMission, setActiveMission] = useState<Mission | null>(null)
  const [isCompletingMission, setIsCompletingMission] = useState(false)
  const [celebrationData, setCelebrationData] = useState<MissionCompleteData | null>(null)
  const [showCelebrationModal, setShowCelebrationModal] = useState(false)

  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load story and steps
  useEffect(() => {
    async function loadStory() {
      if (!storyId) return

      try {
        const result = await repository.getStoryWithSteps(storyId)
        if (result.success && result.data) {
          setStory(result.data.story)
          setSteps(result.data.steps)
          setTitleInput(result.data.story.title)
        } else {
          showToast('Story not found', 'error')
          navigate('/stories')
        }
      } catch (err) {
        console.error('Failed to load story:', err)
        showToast('Could not load story', 'error')
        navigate('/stories')
      } finally {
        setIsLoading(false)
      }
    }

    loadStory()
  }, [storyId, navigate, showToast, repository])

  // Load active mission for this child (if any)
  useEffect(() => {
    async function loadActiveMission() {
      if (!activeWriter || !isSupabaseConfigured()) return

      try {
        const { data: progress } = await supabase
          .from('mission_progress')
          .select('mission_id')
          .eq('child_id', activeWriter.id)
          .is('completed_at', null)
          .order('started_at', { ascending: false })
          .limit(1)
          .single()

        if (progress?.mission_id) {
          const { data: mission } = await supabase
            .from('missions')
            .select('*')
            .eq('id', progress.mission_id)
            .single()

          if (mission) {
            setActiveMission(mission as Mission)
          }
        }
      } catch {
        // No active mission - that's fine
        console.log('No active mission found')
      }
    }

    loadActiveMission()
  }, [activeWriter])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  // Bridge: load story secrets and match to current writing step
  useEffect(() => {
    async function matchSecrets() {
      if (!activeWriter) return

      const secrets = await loadSecrets(activeWriter.id)
      if (secrets.length === 0) {
        setSecretNudge(null)
        return
      }

      const stepTypeToScene: Record<string, WritingSceneType> = {
        beginning: 'opening',
        problem: 'middle',
        adventure: 'middle',
        climax: 'climax',
        resolution: 'resolution',
      }

      const currentStepType = STEP_ORDER[activeStepIndex]
      const sceneType = stepTypeToScene[currentStepType]

      const nudges = getRelevantSecrets(secrets, { sceneType })
      setSecretNudge(nudges.length > 0 ? nudges[0] : null)
    }

    matchSecrets()
  }, [activeWriter, activeStepIndex])

  const activeStep = steps[activeStepIndex]
  const stepConfig = activeStep ? STEP_CONFIG[activeStep.stepType] : null

  // Save step content with debounce
  const saveStepContent = useCallback(
    async (stepType: StepType, content: string) => {
      if (!storyId) return

      // Check for read-only mode
      if (readOnly) {
        showToast('Your subscription has lapsed. Renew to save changes.', 'error')
        return
      }

      setIsSaving(true)
      try {
        const result = await repository.updateStep(storyId, stepType, content)
        if (result.success) {
          setSteps((prev) =>
            prev.map((s) => (s.stepType === stepType ? result.data : s))
          )
          setHasUnsavedChanges(false)
        } else {
          showToast(result.error || 'Could not save. Please try again.', 'error')
        }
      } catch (err) {
        console.error('Failed to save:', err)
        showToast('Could not save. Please try again.', 'error')
      } finally {
        setIsSaving(false)
      }
    },
    [storyId, showToast, repository, readOnly]
  )

  // Handle content change with debounced autosave
  const handleContentChange = (content: string) => {
    if (!activeStep) return

    const wordCount = content.trim().split(/\s+/).filter(Boolean).length

    // Update local state immediately
    setSteps((prev) =>
      prev.map((s) =>
        s.stepType === activeStep.stepType
          ? { ...s, content, wordCount }
          : s
      )
    )
    setHasUnsavedChanges(true)

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Set new timeout for autosave
    saveTimeoutRef.current = setTimeout(() => {
      saveStepContent(activeStep.stepType, content)
    }, AUTOSAVE_DELAY)
  }

  // Handle title save
  const handleTitleSave = async () => {
    if (!story || !titleInput.trim()) return

    if (readOnly) {
      showToast('Your subscription has lapsed. Renew to make changes.', 'error')
      return
    }

    try {
      const result = await repository.updateStory(story.id, { title: titleInput.trim() })
      if (result.success) {
        setStory(result.data)
        setIsEditingTitle(false)
        showToast('Title updated!', 'success')
      } else {
        showToast(result.error || 'Could not update title', 'error')
      }
    } catch (err) {
      console.error('Failed to update title:', err)
      showToast('Could not update title', 'error')
    }
  }

  // Navigation
  const goToPreviousStep = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex(activeStepIndex - 1)
    }
  }

  const goToNextStep = () => {
    if (activeStepIndex < steps.length - 1) {
      setActiveStepIndex(activeStepIndex + 1)
    }
  }

  const handleSwitchWriter = () => {
    setActiveWriter(null)
    navigate('/select-writer')
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/')
  }

  // Complete the active mission
  const handleCompleteMission = async () => {
    if (!activeMission || !activeWriter || !storyId) return

    setIsCompletingMission(true)
    try {
      const result = await completeMissionWithCelebration(
        activeWriter.id,
        activeMission,
        storyId,
        totalWordCount,
        {} // checklist state - empty for now
      )

      if (result.success && result.celebrationData) {
        setActiveMission(null)
        setCelebrationData(result.celebrationData)
        setShowCelebrationModal(true)
      } else {
        showToast(result.error || 'Could not complete mission', 'error')
      }
    } catch (err) {
      console.error('Error completing mission:', err)
      showToast('Something went wrong', 'error')
    } finally {
      setIsCompletingMission(false)
    }
  }

  // Handle closing the celebration modal
  const handleCelebrationClose = () => {
    setShowCelebrationModal(false)
    setCelebrationData(null)
    // Show upgrade banner in local mode after completing a story
    if (mode === 'local') {
      setShowCompletionBanner(true)
    } else {
      navigate('/dashboard?completed=true')
    }
  }

  // Get avatar emoji
  const getAvatarEmoji = (avatarId: string) => {
    const avatars: Record<string, string> = {
      dragon: '🐉',
      unicorn: '🦄',
      robot: '🤖',
      astronaut: '👨‍🚀',
      wizard: '🧙',
      superhero: '🦸',
      cat: '🐱',
      dog: '🐕',
    }
    return avatars[avatarId] || '👤'
  }

  // Calculate total word count
  const totalWordCount = steps.reduce((sum, step) => sum + step.wordCount, 0)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-kids-primary)]" />
      </div>
    )
  }

  if (!story || !activeStep || !stepConfig) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <p className="text-[var(--color-kids-text-muted)]">Story not found</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)] flex flex-col">
      {/* Mission Complete Celebration Modal */}
      <MissionCompleteModal
        isOpen={showCelebrationModal}
        data={celebrationData}
        onClose={handleCelebrationClose}
      />

      {/* Header */}
      <header className="bg-white shadow-sm flex-shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link
              to="/stories"
              className="flex items-center gap-2 text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">My Stories</span>
            </Link>

            <span className="text-[var(--color-kids-border)]">|</span>

            {/* Story title */}
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  className="w-64"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave()
                    if (e.key === 'Escape') {
                      setTitleInput(story.title)
                      setIsEditingTitle(false)
                    }
                  }}
                />
                <Button size="sm" onClick={handleTitleSave}>
                  Save
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingTitle(true)}
                className="flex items-center gap-2 text-[var(--color-kids-text)] hover:text-[var(--color-kids-primary)] transition-colors"
              >
                <span className="text-2xl">{story.coverEmoji}</span>
                <span className="font-display font-bold">{story.title}</span>
                <Pencil className="w-4 h-4 opacity-50" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Studio mode indicator */}
            <StudioModeIndicator size="sm" />

            {/* Save indicator */}
            <div className="flex items-center gap-2 text-sm">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-[var(--color-kids-text-muted)]" />
                  <span className="text-[var(--color-kids-text-muted)]">Saving...</span>
                </>
              ) : hasUnsavedChanges ? (
                <span className="text-[var(--color-kids-text-muted)]">Unsaved changes</span>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 text-[var(--color-kids-success)]" />
                  <span className="text-[var(--color-kids-success)]">Saved</span>
                </>
              )}
            </div>

            <button
              onClick={handleSwitchWriter}
              className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-kid)] hover:bg-[var(--color-kids-surface-alt)] transition-colors"
            >
              <span className="text-xl">{getAvatarEmoji(activeWriter?.avatar_id || 'dragon')}</span>
              <ChevronRight className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
            </button>

            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col max-w-5xl mx-auto w-full p-6">
        {/* Upgrade banner after completing story in local mode */}
        {showCompletionBanner && (
          <div className="mb-6">
            <UpgradeBanner
              variant="after-completion"
              onDismiss={() => {
                setShowCompletionBanner(false)
                navigate('/dashboard?completed=true')
              }}
            />
          </div>
        )}

        {/* Step navigation */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEP_ORDER.map((stepType, index) => {
            const step = steps.find((s) => s.stepType === stepType)
            const config = STEP_CONFIG[stepType]
            const isActive = index === activeStepIndex
            const hasContent = step && step.wordCount > 0

            return (
              <button
                key={stepType}
                onClick={() => setActiveStepIndex(index)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-full transition-all
                  ${
                    isActive
                      ? 'bg-white shadow-md scale-105'
                      : 'hover:bg-white/50'
                  }
                `}
                style={{
                  borderWidth: 2,
                  borderStyle: 'solid',
                  borderColor: isActive ? config.color : 'transparent',
                }}
              >
                <span className="text-lg">{config.emoji}</span>
                <span
                  className={`font-medium text-sm ${
                    isActive
                      ? 'text-[var(--color-kids-text)]'
                      : 'text-[var(--color-kids-text-muted)]'
                  }`}
                >
                  {config.label}
                </span>
                {hasContent && (
                  <CheckCircle
                    className="w-4 h-4"
                    style={{ color: config.color }}
                  />
                )}
              </button>
            )
          })}
        </div>

        {/* Editor area */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col"
          >
            <Card className="flex-1 flex flex-col">
              <CardContent className="flex-1 flex flex-col p-6">
                {/* Step header */}
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{stepConfig.emoji}</span>
                    <h2
                      className="font-display text-2xl font-bold"
                      style={{ color: stepConfig.color }}
                    >
                      {stepConfig.label}
                    </h2>
                  </div>
                  <p className="text-[var(--color-kids-text-muted)]">
                    {stepConfig.prompt}
                  </p>
                </div>

                {/* Textarea */}
                <div className="flex-1 relative">
                  <textarea
                    value={activeStep.content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder="Start writing here..."
                    className="
                      w-full h-full min-h-[300px] p-4
                      bg-[var(--color-kids-surface-alt)]
                      border-2 border-transparent
                      focus:border-[var(--color-kids-primary)]
                      rounded-[var(--radius-kid)]
                      font-body text-lg text-[var(--color-kids-text)]
                      placeholder:text-[var(--color-kids-text-muted)]
                      resize-none outline-none transition-colors
                    "
                    style={{ lineHeight: 1.8 }}
                  />
                </div>

                {/* Story Secret nudge (bridge from storytelling mode) */}
                {secretNudge && (
                  <div className="mt-4">
                    <StorySecretNudge nudge={secretNudge} />
                  </div>
                )}

                {/* Footer stats */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--color-kids-border)]">
                  <div className="flex items-center gap-6 text-sm text-[var(--color-kids-text-muted)]">
                    <span>
                      This step: <strong>{activeStep.wordCount}</strong> words
                    </span>
                    <span>
                      Total story: <strong>{totalWordCount}</strong> words
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goToPreviousStep}
                      disabled={activeStepIndex === 0}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      onClick={goToNextStep}
                      disabled={activeStepIndex === steps.length - 1}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>

                    {/* Complete Mission button */}
                    {activeMission && (
                      <Button
                        size="sm"
                        onClick={handleCompleteMission}
                        disabled={isCompletingMission || totalWordCount === 0}
                        className="ml-4 bg-[var(--color-kids-success)] hover:bg-[var(--color-kids-success)]/90"
                      >
                        {isCompletingMission ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Trophy className="w-4 h-4 mr-1" />
                        )}
                        Complete Mission
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  )
}
